import { useEffect, useState } from "react";
import {
  getDeviationsAllTime,
  addHerca,
  updateHerca,
  deleteHerca,
  HercaRecord,
  Employee,
  Area,
  DeviationType,
} from "../api";
import { api } from "../api/client";
import Modal from "../components/common/Modal";
import "../components/common/Modal.css";
import ConfirmDialog from "../components/common/ConfirmDialog";
import Spinner from "../components/common/Spinner";
import "./Page.css";

const emptyForm = {
  employeeId: "",
  areaId: "",
  deviationGapId: "",
  incidentDate: "",
  interviewDate: "",
  description: "",
  interviewer: "",
  deviationRecordCode: "",
};

function toDateInput(iso: string) {
  return iso ? new Date(iso).toISOString().split("T")[0] : "";
}

type SortField =
  | "index"
  | "deviationRecordCode"
  | "employeeName"
  | "areaName"
  | "deviationName"
  | "interviewerName"
  | "incidentDate"
  | "interviewDate";
type SortDir = "asc" | "desc";

function getSortValue(r: HercaRecord, field: SortField): string | number {
  switch (field) {
    case "index":
      return r.index;
    case "deviationRecordCode":
      return r.deviationRecordCode ?? 0;
    case "employeeName":
      return r.employeeId?.name ?? "";
    case "areaName":
      return r.areaId?.name ?? "";
    case "deviationName":
      return r.deviationGapId?.name ?? "";
    case "interviewerName":
      return r.interviewer?.name ?? "";
    case "incidentDate":
      return new Date(r.incidentDate).getTime();
    case "interviewDate":
      return new Date(r.interviewDate).getTime();
  }
}

const HercaRecords = () => {
  const [records, setRecords] = useState<HercaRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [deviationTypes, setDeviationTypes] = useState<DeviationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState<HercaRecord | null>(null);
  const [sortField, setSortField] = useState<SortField>("index");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const isDefaultSort = sortField === "index" && sortDir === "asc";

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const clearSort = () => {
    setSortField("index");
    setSortDir("asc");
  };

  const sortIcon = (field: SortField) => {
    const active = sortField === field;
    const asc = active && sortDir === "asc";
    const desc = active && sortDir === "desc";
    return (
      <svg
        className="sort-svg"
        width="12"
        height="16"
        viewBox="0 0 12 16"
        fill="none"
      >
        <path d="M6 2L2 7H10L6 2Z" fill={asc ? "#7B2D8B" : "#D5A8E8"} />
        <path d="M6 14L2 9H10L6 14Z" fill={desc ? "#7B2D8B" : "#D5A8E8"} />
      </svg>
    );
  };

  const load = () => {
    setLoading(true);
    Promise.all([
      getDeviationsAllTime(),
      api.get<Employee[]>("/admin/employees").catch(() => []),
      api.get<DeviationType[]>("/admin/deviations").catch(() => []),
    ])
      .then(([data, emps, devs]) => {
        setRecords(data.deviations ?? []);
        setEmployees(emps as Employee[]);
        setDeviationTypes(devs as DeviationType[]);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setShowModal(true);
  };

  const openEdit = (r: HercaRecord) => {
    setEditingId(r._id);
    setForm({
      employeeId: r.employeeId?._id ?? "",
      areaId: r.areaId?._id ?? "",
      deviationGapId: r.deviationGapId?._id ?? "",
      incidentDate: toDateInput(r.incidentDate),
      interviewDate: toDateInput(r.interviewDate),
      description: r.description,
      interviewer: r.interviewer?._id ?? "",
      deviationRecordCode: String(r.deviationRecordCode ?? ""),
    });
    setFormError("");
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteHerca(confirmDelete._id);
      setConfirmDelete(null);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete record");
      setConfirmDelete(null);
    }
  };

  const handleSubmit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        deviationRecordCode: Number(form.deviationRecordCode),
      };
      if (editingId) {
        await updateHerca(editingId, payload);
      } else {
        await addHerca(payload);
      }
      setShowModal(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : "Failed to save record");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = records
    .filter((r) => {
      const q = search.toLowerCase();
      return (
        r.employeeId?.name?.toLowerCase().includes(q) ||
        r.areaId?.name?.toLowerCase().includes(q) ||
        r.deviationGapId?.name?.toLowerCase().includes(q) ||
        String(r.index).includes(q) ||
        String(r.deviationRecordCode).includes(q)
      );
    })
    .slice()
    .sort((a, b) => {
      const av = getSortValue(a, sortField);
      const bv = getSortValue(b, sortField);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const selectedEmp = employees.find((e) => e._id === form.employeeId);
  const empAreas = ((selectedEmp?.areas ?? []) as Area[]).filter(
    (a): a is Area => typeof a !== "string",
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Herca Records</h1>
          <p className="page-subtitle">{records.length} total records</p>
        </div>
        <button className="btn-primary" onClick={openAdd}>
          + New Record
        </button>
      </div>

      <div className="page-toolbar">
        <input
          className="search-input"
          placeholder="Search by employee, area, deviation..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!isDefaultSort && (
          <button className="btn-clear-sort" onClick={clearSort}>
            ✕ Clear Sort
          </button>
        )}
      </div>

      {loading && <Spinner />}
      {error && <p className="error-banner">{error}</p>}

      {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th className="th-sortable" onClick={() => handleSort("index")}>
                  # {sortIcon("index")}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => handleSort("deviationRecordCode")}
                >
                  Record Code {sortIcon("deviationRecordCode")}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => handleSort("employeeName")}
                >
                  Employee {sortIcon("employeeName")}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => handleSort("areaName")}
                >
                  Area {sortIcon("areaName")}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => handleSort("deviationName")}
                >
                  Deviation Gap {sortIcon("deviationName")}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => handleSort("interviewerName")}
                >
                  Interviewer {sortIcon("interviewerName")}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => handleSort("incidentDate")}
                >
                  Incident Date {sortIcon("incidentDate")}
                </th>
                <th
                  className="th-sortable"
                  onClick={() => handleSort("interviewDate")}
                >
                  Interview Date {sortIcon("interviewDate")}
                </th>
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty-cell">
                    No records found
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r._id}>
                    <td>
                      <span className="index-badge">{r.index}</span>
                    </td>
                    <td>{r.deviationRecordCode}</td>
                    <td>{r.employeeId?.name}</td>
                    <td>
                      <span className="area-tag">{r.areaId?.name}</span>
                    </td>
                    <td>{r.deviationGapId?.name}</td>
                    <td>{r.interviewer?.name}</td>
                    <td>
                      {new Date(r.incidentDate).toLocaleDateString("en-GB")}
                    </td>
                    <td>
                      {new Date(r.interviewDate).toLocaleDateString("en-GB")}
                    </td>
                    <td className="desc-cell">{r.description}</td>
                    <td>
                      <button className="btn-sm" onClick={() => openEdit(r)}>
                        Edit
                      </button>
                      <button
                        className="btn-sm btn-danger"
                        style={{ marginLeft: 8 }}
                        onClick={() => setConfirmDelete(r)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={
            editingId
              ? `Edit Record #${records.find((r) => r._id === editingId)?.index}`
              : "Add Herca Record"
          }
          onClose={() => setShowModal(false)}
        >
          <form onSubmit={handleSubmit}>
            {formError && <p className="form-error">{formError}</p>}

            <div className="form-field">
              <label>Deviation Record Code</label>
              <input
                type="number"
                value={form.deviationRecordCode}
                onChange={(e) =>
                  setForm({ ...form, deviationRecordCode: e.target.value })
                }
                required
                min={1}
                max={99999999999}
                placeholder="Up to 11 digits"
              />
            </div>

            <div className="form-field">
              <label>Employee</label>
              <select
                value={form.employeeId}
                onChange={(e) => {
                  const emp = employees.find(
                    (emp) => emp._id === e.target.value,
                  );
                  const mainAreaId =
                    (emp?.mainArea as unknown as Area)?._id ?? "";
                  setForm({
                    ...form,
                    employeeId: e.target.value,
                    areaId: mainAreaId,
                  });
                }}
                required
              >
                <option value="">Select employee</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}{emp.employeeType && emp.employeeType !== 'regular' ? ` (${emp.employeeType === 'ex-employee' ? 'Ex-Employee' : 'Other DCs'})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Area</label>
              <select
                value={form.areaId}
                onChange={(e) => setForm({ ...form, areaId: e.target.value })}
                required
                disabled={!form.employeeId}
              >
                <option value="">
                  {form.employeeId ? "Select area" : "Select employee first"}
                </option>
                {empAreas.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {form.employeeId && empAreas.length === 0 && (
                <span style={{ color: "#C0392B", fontSize: 12, marginTop: 4 }}>
                  This employee has no assigned areas. Assign areas first.
                </span>
              )}
            </div>

            <div className="form-field">
              <label>Deviation Gap</label>
              <select
                value={form.deviationGapId}
                onChange={(e) =>
                  setForm({ ...form, deviationGapId: e.target.value })
                }
                required
              >
                <option value="">Select deviation type</option>
                {deviationTypes.map((d) => (
                  <option key={d._id} value={d._id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Interviewer</label>
              <select
                value={form.interviewer}
                onChange={(e) =>
                  setForm({ ...form, interviewer: e.target.value })
                }
                required
              >
                <option value="">Select interviewer</option>
                {employees
                  .filter((e) => e.canMakeInterview)
                  .map((e) => (
                    <option key={e._id} value={e._id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Incident Date</label>
                <input
                  type="date"
                  value={form.incidentDate}
                  onChange={(e) =>
                    setForm({ ...form, incidentDate: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-field">
                <label>Interview Date</label>
                <input
                  type="date"
                  value={form.interviewDate}
                  onChange={(e) =>
                    setForm({ ...form, interviewDate: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="form-field">
              <label>Description</label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                required
                placeholder="Describe the deviation..."
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : editingId
                    ? "Update Record"
                    : "Save Record"}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete Herca Record"
          message={`Are you sure you want to delete record #${confirmDelete.index}? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default HercaRecords;
